import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  type AssetFilters,
  assetQuery,
  useAssetLibrary,
  useDeleteAsset,
} from "../api/assetlib";
import { ApiError } from "../api/client";
import { useMe } from "../api/me";
import type { AssetItem } from "../api/types";
import { messageFor } from "../copy/errors";
import { ErrorLine } from "../ui/ErrorLine";
import { Button, Skeleton } from "../ui";
import { AssetCard, type CardSelect } from "./AssetCard";
import { categoryWord } from "./categories";
import { deleteConfirmText } from "./deleteConfirm";

const GRID =
  "m-0 grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 lg:grid-cols-4";
// A picker sits in a window at most 640 px wide: three columns there.
const PICKER_GRID = "m-0 grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3";
const SKELETONS = 12;

/** "Nothing of your own yet." names only what the user can do: importing and
 *  designing are mentioned when those features are on. */
function NothingOfYourOwn() {
  const features = useMe().data?.features;
  const importIt = (
    <Link to="/assets/import" className="underline">
      import a model
    </Link>
  );
  const designIt = (
    <Link to="/assets/studio/new" className="underline">
      design one
    </Link>
  );
  return (
    <p className="m-0 text-slate-700" data-testid="assets-empty">
      Nothing of your own yet.{" "}
      <Link to="/assets" className="underline">
        Save a look from any object
      </Link>
      {features?.imports && features?.studio ? (
        <>
          , {importIt}, or {designIt}.
        </>
      ) : features?.imports ? (
        <>, or {importIt}.</>
      ) : features?.studio ? (
        <>, or {designIt}.</>
      ) : (
        "."
      )}
    </p>
  );
}

function cardSelect(select: GridSelect, item: AssetItem): CardSelect {
  const pressed = (select.selected ?? []).includes(`${item.kind}:${item.id}`);
  return {
    pressed,
    disabled: select.full === true && !pressed,
    title: `Up to ${select.max ?? 8} objects`,
    onSelect: select.onSelect,
  };
}

/** One category the list counted: what the page's category filter offers. */
export interface CategoryCount {
  id: string;
  count: number;
}

/** The cards in runs under one heading: the organisation's own first ("Yours"),
 *  then each category the curated ones are listed by. */
function sections(items: AssetItem[]): { title: string; items: AssetItem[] }[] {
  const found: { title: string; items: AssetItem[] }[] = [];
  for (const item of items) {
    const title = item.tier === "org" ? "Yours" : categoryWord(item.category);
    const last = found[found.length - 1];
    if (last && last.title === title) last.items.push(item);
    else found.push({ title, items: [item] });
  }
  return found;
}

/** The grid as a picker ([library-section §10.5]): every card is a toggle
 *  button. `selected` holds `"<kind>:<id>"` strings; with `full` every card
 *  that is not pressed is disabled, with the title "Up to `max` objects". */
export interface GridSelect {
  kinds: ("shape" | "variant" | "figure")[];
  onSelect(item: AssetItem): void;
  selected?: string[];
  full?: boolean;
  /** Default 8. */
  max?: number;
}

/** The grid of the 3D library for one set of filters: skeletons, a failure
 *  with **Try again**, the cards with **Load more**, or the sentence of the
 *  empty state the filters describe. Every card has its star. The
 *  organisation's own cards can be deleted from their ⋯ menu; `onDeleted` tells the page when one was. With
 *  `select` it is a picker: no links, no stars and no menus. */
export function AssetGrid(props: {
  filters: AssetFilters;
  select?: GridSelect;
  /** A heading over each category's run of cards, while the list is in its own
   *  order (no search, no category chosen). */
  headings?: boolean;
  /** What the list counted for each category. */
  onCategories?: (counts: CategoryCount[]) => void;
  onClearSearch?: () => void;
  onDeleted?: () => void;
}): JSX.Element {
  const { filters, select, headings, onCategories, onClearSearch, onDeleted } =
    props;
  const library = useAssetLibrary(filters);
  // A search with no match can be designed, where the design studio is on.
  const studio = useMe().data?.features?.studio === true;
  const counted = library.data?.pages[0]?.categories;
  useEffect(() => {
    if (onCategories && counted) onCategories(counted);
  }, [counted, onCategories]);
  const queryClient = useQueryClient();
  const remove = useDeleteAsset();
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  // A list item has no counts, so the question is asked from the detail.
  const onDelete = async (item: AssetItem) => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const detail = await queryClient.fetchQuery(
        assetQuery(item.kind, item.id),
      );
      if (!window.confirm(deleteConfirmText(detail))) return;
      await remove.mutateAsync({ kind: item.kind, id: item.id });
      onDeleted?.();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  if (library.isPending) {
    return (
      <ul className={select ? PICKER_GRID : GRID} aria-busy="true">
        {Array.from({ length: SKELETONS }).map((_, i) => (
          <li key={i} data-testid="asset-skeleton">
            <span className="block aspect-square w-full overflow-hidden rounded-lg">
              <Skeleton width="100%" height="100%" />
            </span>
            <span className="mt-2 block">
              <Skeleton width="70%" height={14} />
            </span>
            <span className="mt-1 block">
              <Skeleton width="45%" height={12} />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  if (library.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="m-0 text-slate-700">
          {messageFor(
            library.error instanceof ApiError ? library.error.code : undefined,
          )}
        </p>
        <Button
          type="button"
          className="min-h-[44px] sm:min-h-0"
          onClick={() => void library.refetch()}
        >
          Try again
        </Button>
      </div>
    );
  }

  const all = library.data.pages.flatMap((page) => page.items);
  const kinds: AssetItem["kind"][] | undefined = select?.kinds;
  const items = kinds ? all.filter((item) => kinds.includes(item.kind)) : all;
  if (items.length === 0) {
    if (filters.q) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p className="m-0 text-slate-700" data-testid="assets-empty">
            Nothing matches &quot;{filters.q}&quot;. Try a more general word, or
            describe the object in your ad and it will be modelled for you.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {onClearSearch ? (
              <Button
                type="button"
                className="min-h-[44px] sm:min-h-0"
                onClick={onClearSearch}
              >
                Clear search
              </Button>
            ) : null}
            {studio && !select ? (
              <Link
                to={`/assets/studio/new?describe=${encodeURIComponent(filters.q)}`}
                className="inline-flex min-h-[44px] items-center text-sm underline sm:min-h-0"
              >
                Design it yourself
              </Link>
            ) : null}
          </div>
        </div>
      );
    }
    if (filters.tier === "org") return <NothingOfYourOwn />;
    if (filters.favorites) {
      return (
        <p className="m-0 text-slate-700" data-testid="assets-empty">
          No favourites yet. Press the star on anything you use often; TrueReel
          prefers your favourites when it writes an ad.
        </p>
      );
    }
    return (
      <p className="m-0 text-slate-700" data-testid="assets-empty">
        Nothing here yet.
      </p>
    );
  }

  const cardOf = (item: AssetItem) => (
    <li key={`${item.kind}:${item.id}`}>
      {select ? (
        <AssetCard item={item} select={cardSelect(select, item)} />
      ) : (
        <AssetCard item={item} onDelete={(it) => void onDelete(it)} />
      )}
    </li>
  );
  const grouped =
    headings &&
    !select &&
    !filters.q &&
    !filters.category &&
    !filters.favorites;
  return (
    <div className="flex flex-col gap-6">
      {failure ? <ErrorLine error={failure} context="asset_delete" /> : null}
      {grouped ? (
        sections(items).map((section, index) => (
          <section
            key={`${index}:${section.title}`}
            aria-label={section.title}
            data-testid="asset-section"
          >
            <h2 className="tb-h-row mb-3 mt-0">{section.title}</h2>
            <ul className={GRID}>{section.items.map(cardOf)}</ul>
          </section>
        ))
      ) : (
        <ul className={select ? PICKER_GRID : GRID}>{items.map(cardOf)}</ul>
      )}
      {library.hasNextPage ? (
        <Button
          type="button"
          variant="ghost"
          className="min-h-[44px] w-full sm:min-h-0 sm:w-auto sm:self-center"
          disabled={library.isFetchingNextPage}
          aria-busy={library.isFetchingNextPage}
          onClick={() => void library.fetchNextPage()}
        >
          {library.isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      ) : null}
    </div>
  );
}
